import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { Spinner } from 'components/Loaders/Loaders'
import { getAmendmentFilingWizards, getFilingWizards } from './PaperModalActions'
import DocumentIcon from '@tecuity/sos-icons/react/document'
import ICONS from 'components/Icons/Icons'
import { ContextBar } from '@tecuity/granite-ui/ContextBar'
import { ContextButton } from '@tecuity/granite-ui/ContextButton'
import { SearchBox } from '@tecuity/granite-ui/SearchBox'
import Cross from '@tecuity/sos-icons/react/cross-heavy'
import '../WorkviewManager.scss'
import * as api from '../../../requests'
import {
  SET_RECORD_TO_AMEND,
  SET_AMENDMENT_WIZARDS,
  SET_PAPER_FILING_ERROR_MESSAGE,
  SET_SELECTED_FORM,
} from '../../WVManager/Workview2Reducer'
import { FilingSourceTypeIds} from '../../../Utilities/Utilities'
import { useHistory } from 'react-router'
import { setErrorToast } from '../../../actions'

const FormSelectionList = () => {
  const {
    selectedRow,
    loadingAmendmentWizards,
    amendmentWizards,
    staffDraft,
    showPaperFilingErrorMessage,
    paperFilingErrorMessage,
    businessForms,
    lienForms,
    notaryForms,
    registeredAgentForms,
    trademarkForms,
    athleteAgentForms,
  } = useSelector((state) => state.workview2, shallowEqual)
  const [formFilter, setFormFilter] = useState('')
  const [loadingForms, setLoadingForms] = useState(false)
  const [localForms, setLocalForms] = useState([])
  const dispatch = useDispatch()
  const history = useHistory()

  useEffect(() => {
    if (!loadingAmendmentWizards && selectedRow.IsInitialFiling){
      switch (selectedRow.SourceTypeId){
        case FilingSourceTypeIds.Charity:
        case FilingSourceTypeIds.Contractor:
        case FilingSourceTypeIds.Lobbyist:
          setLoadingForms(true)
          api.GET_FORMS(
          selectedRow.SourceTypeId,
        (forms) => {
          setLoadingForms(false)
          setLocalForms(forms)
        },
        (error) => {
          // Todo add toast
          dispatch(setErrorToast(`Failed to load forms.`))
          setLoadingForms(false)
          console.error(error)
        })
        break;
      default:
          break;
    }
  }
  }, [selectedRow])

  const categoryForms = useMemo(() => {
    if (!selectedRow.IsInitialFiling && selectedRow.RecordToAmend && amendmentWizards.length > 0)
      return amendmentWizards
    switch (selectedRow.SourceTypeId) {
      case 169:
        return businessForms
      case 462:
        return registeredAgentForms
      case 2000:
        return lienForms
      case 811:
        return trademarkForms
      case 851:
        return notaryForms
      case FilingSourceTypeIds.AthleteAgent:
        return athleteAgentForms
      // Henceforth, when new modules are added do it this way NOT the Redux way above
      case FilingSourceTypeIds.Charity:
      case FilingSourceTypeIds.Contractor:
      case FilingSourceTypeIds.Lobbyist:
        return localForms
      default:
        return []
    }
  }, [
    selectedRow,
    amendmentWizards,
    localForms
  ])

  const filteredWizards = useMemo(() => {
    if (loadingAmendmentWizards) {
      return []
    }
    const wizardList = []
    const FormDefinitionIDs = new Set()
    categoryForms.forEach((option) => {
      option.forms.forEach((form) => {
        if (form.IsInitialFiling === selectedRow.IsInitialFiling) {
          if (!FormDefinitionIDs.has(form.FormDefinitionID)) {
            if (!formFilter || form.FormName.toLowerCase().includes(formFilter.toLowerCase())) {
              const wizard = { ...form }
              if (
                selectedRow.SourceTypeId === FilingSourceTypeIds.Business &&
                selectedRow.IsInitialFiling
              )
                wizard.filingTypeID = option.value
              else wizard.amendmentTypeID = option.value

              wizardList.push(wizard)
              FormDefinitionIDs.add(form.FormDefinitionID)
            }
          }
        }
      })
    })
    return wizardList.sort((a, b) => {
      return a.FormName > b.FormName ? 1 : -1
    })
  }, [categoryForms, formFilter, loadingAmendmentWizards, loadingForms])

  useEffect(() => {
    if (selectedRow.RecordToAmend) {
      const item = selectedRow.RecordToAmend

      const identifier = item['File #'] ||
        item['Commission Number'] ||
        item['Filing #'] ||
        item['Filing Number'] ||
        item['Record Number'] ||
        item['Trademark #'] ||
        item['License #'] ||
        item['SourceID']

      if(identifier === null || identifier === undefined || identifier === ""){
        dispatch(getFilingWizards(item["SourceType"]))
      }else{
        dispatch(
          getAmendmentFilingWizards(identifier)
        )
      }
    }
  }, [selectedRow.RecordToAmend])

  const secondaryReview = useMemo(() => {
    // if a paper filed WVItem has a filingNum and a formDefinitionId,
    // then it must have come back from secondary review.
    if (selectedRow.Filing.Num && selectedRow.FormDefinitionId) {
      return {
        FilingNum: selectedRow.Filing.Num,
        FormDefinitionId: selectedRow.FormDefinitionId,
        ImagePageType: selectedRow.ResourceImagePageType,
      }
    } else return null
  }, [selectedRow])

  return (
    <div>
      {!selectedRow.IsInitialFiling && selectedRow.RecordToAmend && (
        <div
          style={{
            textAlign: 'center',
            minWidth: '400px',
            padding: '15px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <ContextBar elevation="high">
            <ContextButton
              leftIcon={
                <div className="button-icon">
                  {' '}
                  <Cross />{' '}
                </div>
              }
              variant="danger"
              onClick={() => {
                dispatch({ type: SET_RECORD_TO_AMEND, record: null })
                dispatch({
                  type: SET_AMENDMENT_WIZARDS,
                  amendmentWizards: [],
                  loadingAmendmentWizards: false,
                })
                dispatch({ type: SET_PAPER_FILING_ERROR_MESSAGE, paperFilingErrorMessage: '' })
              }}
              customCss={''}
            >
              Cancel
            </ContextButton>
            <span className="primary">
              <h1 style={{ fontWeight: 'bold' }}>Amending:</h1>{' '}
              {selectedRow.RecordToAmend.Name ||
                selectedRow.RecordToAmend['Trademark Name'] ||
                selectedRow.RecordToAmend['Doc #'] ||
                selectedRow.RecordToAmend['Notary Name'] ||
                selectedRow.RecordToAmend['Filing Number'] ||
                selectedRow.RecordToAmend['Record Number'] ||
                selectedRow.RecordToAmend['Filing #'] ||
                selectedRow.RecordToAmend['License #']}
            </span>
          </ContextBar>
        </div>
      )}
      <div className="filingSelectButtonWrapper" style={{ marginTop: '8px' }}>
        <SearchBox
          placeholder={'Filter by form name'}
          value={formFilter}
          onChange={(e) => setFormFilter(e)}
          showClearButton={false}
          showSearchButton={false}
        />
      </div>
      {showPaperFilingErrorMessage ? (
        <div
          style={{
            textAlign: 'center',
            width: '100%',
            height: '92.4px',
          }}
        >
          <h1 style={{ padding: '15px', fontWeight: 'bold' }}>{paperFilingErrorMessage}</h1>
        </div>
      ) : (
        <div className="icon-wrapper">
          {filteredWizards.length > 0 && (
            <React.Fragment>
              {staffDraft && (
                <React.Fragment>
                  <h2>Continue working from draft</h2>
                  <div className="form-blocks paper-processed-wizard-item-wrapper">
                    <FormSelectionIcon
                      staffDraft
                      formName={staffDraft.FormTitle}
                      value={staffDraft.FormDefinitionId}
                      selectedRow={selectedRow}
                      form={{
                        FormDefinitionID: staffDraft.FormDefinitionId,
                        FormTitle: staffDraft.FormTitle,
                        filingTypeID: staffDraft.FilingTypeId,
                        amendmentTypeID: staffDraft.AmendmentTypeId,
                        IsInitialFiling: !!staffDraft.AmendmentTypeId,
                      }}
                    />
                  </div>
                </React.Fragment>
              )}
              {secondaryReview && (
                <React.Fragment>
                  <h2>Review Debtors/Secured Parties</h2>
                  <div className="form-blocks paper-processed-wizard-item-wrapper">
                    <FormSelectionIcon
                      staffDraft
                      formName={secondaryReview.ImagePageType}
                      value={secondaryReview.FormDefinitionId}
                      selectedRow={selectedRow}
                      processInitialFiling={() => {
                        // reopen wizard with filing info
                        history.push(
                          `/office/workview/process/${secondaryReview.FormDefinitionId}/${secondaryReview.FilingNum}?staffEntryRequired=true`
                        )
                      }}
                    />
                  </div>
                </React.Fragment>
              )}
              <h2>{staffDraft ? 'Discard draft and select a new form' : 'Select a form'}</h2>
              <div className="form-blocks paper-processed-wizard-item-wrapper">
                {filteredWizards.map((item, i) => (
                  <FormSelectionIcon
                    key={i}
                    formName={item.FormName}
                    value={item.FormDefinitionID}
                    selectedRow={selectedRow}
                    form={item}
                  />
                ))}
              </div>
            </React.Fragment>
          )}
          {(loadingAmendmentWizards || loadingForms) && (
            <div
              style={{
                height: '200px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              className="form-blocks"
            >
              <div>
                <Spinner className="page-loader" themeColor="neutral" />
              </div>
            </div>
          )}
          {!loadingAmendmentWizards &&
            !selectedRow.IsInitialFiling &&
            selectedRow.RecordToAmend &&
            filteredWizards.length === 0 &&
            !formFilter && (
              <div
                style={{
                  textAlign: 'center',
                  width: '100%',
                  height: '92.4px',
                }}
              >
                <h1 style={{ padding: '15px', fontWeight: 'bold' }}>
                  No forms configured to file against record.
                </h1>
              </div>
            )}
          {filteredWizards.length === 0 && formFilter && (
            <div
              style={{
                textAlign: 'center',
                width: '100%',
                height: '92.4px',
              }}
            >
              <h1 style={{ padding: '15px', fontWeight: 'bold' }}>
                No forms match filtration criteria...
              </h1>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export const FormSelectionIcon = ({
  style = {},
  className = '',
  formName = '',
  value,
  selectedRow,
  staffDraft,
  form = {},
  processInitialFiling,
  icon = ICONS['capital'],
}) => {
  const dispatch = useDispatch()

  const clickProcessItem = useCallback(
    (value) => {
      if (processInitialFiling) {
        processInitialFiling()
      } else {
        dispatch({
          type: SET_SELECTED_FORM,
          data: {
            staffDraft: staffDraft,
            formDefinitionID: value,
            filingTypeID: form.filingTypeID,
            amendmentTypeID: form.amendmentTypeID,
          },
        })
      }
    },
    [selectedRow, processInitialFiling]
  )

  return (
    <div
      role="button"
      className={`form-block-wrapper paper-processed-wizard-item`}
      onClick={() => clickProcessItem(value)}
    >
      <div className="form-block">
        <div className={`form-block-icon-container ${className}`} style={style}>
          <div className="abbr-wrapper">
            <div className="abbr">{icon}</div>
            <DocumentIcon />
          </div>
        </div>
        <h3>
          <span className="title"> {formName} </span>
        </h3>
      </div>
    </div>
  )
}

export default FormSelectionList
